if (!customElements.get('tw-review-carousel')) {
  customElements.define('tw-review-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.source = this.querySelector('.tw-review-carousel__source') || this.closest('.tw-review-scope')?.querySelector('#' + this.dataset.sourceId);
      if (!this.source) return;
      this.track = this.querySelector('.tw-review-carousel__track');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.status = this.querySelector('.tw-review-carousel__position');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.queuePosition(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.observer = new MutationObserver(() => {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = requestAnimationFrame(() => this.render());
      });
      this.observer.observe(this.source, { childList: true, subtree: true, characterData: true });
      this.resizeObserver = new ResizeObserver(() => this.queuePosition());
      this.resizeObserver.observe(this.track);
      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.abort?.abort();
      cancelAnimationFrame(this.renderFrame);
      cancelAnimationFrame(this.positionFrame);
      this.observer = null;
      this.signature = null;
      this.closest('.tw-review-scope')?.classList.remove('tw-review-carousel-ready');
    }

    text(item, selector) {
      return item.querySelector(selector)?.textContent.trim() || '';
    }

    node(tag, className, text) {
      const element = document.createElement(tag);
      element.className = className;
      if (text) element.textContent = text;
      return element;
    }

    render() {
      let items = [...this.source.querySelectorAll('.jdgm-ssr-reviews__list .jdgm-rev')];
      if (!items.length) items = [...this.source.querySelectorAll('.jm-review-widget__body .jm-review-item')];
      if (!items.length) items = [...this.source.querySelectorAll('.jdgm-rev-widg__reviews .jdgm-rev')];
      const reviews = items.map((item) => {
        const rating = item.querySelector('.jm-star-rating')?.getAttribute('aria-label');
        const legacy = item.querySelector('.jdgm-rev__rating')?.getAttribute('data-score');
        return {
          score: Math.max(0, Math.min(5, parseInt(rating || legacy || '0', 10))),
          rating,
          author: this.text(item, '.jm-reviewer-info__name, .jdgm-rev__author'),
          date: this.text(item, '.jm-reviewer-info__details > .jm-cluster:last-child, .jdgm-rev__timestamp') || (item.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') || '').slice(0, 10),
          title: this.text(item, '.jm-review-content__title, .jdgm-rev__title'),
          body: this.text(item, '.jdgm-review-content__body-content, .jm-review-content__body, .jdgm-rev__body')
        };
      }).filter(review => review.body);
      const signature = JSON.stringify(reviews);
      if (signature === this.signature) return;
      this.signature = signature;
      this.hidden = !reviews.length;
      this.closest('.tw-review-scope').classList.toggle('tw-review-carousel-ready', reviews.length > 0);
      if (!reviews.length) return;
      const cards = reviews.map((review) => {
        const card = this.node('article', 'tw-review-card');
        const stars = this.node('div', 'tw-review-card__stars', '★'.repeat(review.score) + '☆'.repeat(5 - review.score));
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', review.rating || String(review.score) + '/5');
        card.append(stars);
        if (review.title) card.append(this.node('p', 'tw-review-card__title', review.title));
        if (review.body.length > 380) {
          const excerpt = this.node('p', 'tw-review-card__body tw-review-card__excerpt', review.body.slice(0, 300).replace(/\s+\S*$/, '') + '…');
          const details = this.node('details', 'tw-review-card__details');
          details.append(this.node('summary', '', this.dataset.moreLabel), this.node('p', 'tw-review-card__body', review.body));
          details.addEventListener('toggle', () => { excerpt.hidden = details.open; });
          card.append(excerpt, details);
        } else {
          card.append(this.node('p', 'tw-review-card__body', review.body));
        }
        if (review.author) card.append(this.node('cite', 'tw-review-card__author', review.author));
        if (review.date) card.append(this.node('p', 'tw-review-card__date', review.date));
        return card;
      });
      this.track.replaceChildren(...cards);
      this.track.scrollLeft = 0;
      this.queuePosition();
    }

    move(direction) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
    }

    queuePosition() {
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = requestAnimationFrame(() => this.updatePosition());
    }

    updatePosition() {
      const cards = [...this.track.children];
      if (!cards.length) return;
      const trackRect = this.track.getBoundingClientRect();
      const visible = cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
        .filter(({ rect }) => rect.right > trackRect.left + 10 && rect.left < trackRect.right - 10);
      if (!visible.length) return;
      this.previous.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      this.status.textContent = this.dataset.positionTemplate
        .replace('[first]', visible[0].index + 1)
        .replace('[last]', visible[visible.length - 1].index + 1)
        .replace('[total]', cards.length);
    }
  });
}
